import type { ReactNode } from "react";
import { Link } from "react-router";

/**
 * Where a person opened a file from, so its screen can send them back there.
 *
 * The viewer's back link used to go to `/brain`, which redirects to Ask. A
 * person who opened a file from a shelf, or from the review after an import,
 * landed somewhere they had not been (found while mapping the code for Phase
 * 3f, 2026-09-22). This rides on the router's location state rather than the
 * URL, so a link someone copies still opens the plain file.
 */
export interface CameFrom {
  path: string;
  /** What the back link says: "Back to the review". */
  label: string;
}

/** Read a `CameFrom` out of location state, or null when there is none. */
export function cameFrom(state: unknown): CameFrom | null {
  if (!state || typeof state !== "object" || !("from" in state)) return null;
  const from = (state as { from: unknown }).from;
  if (!from || typeof from !== "object") return null;
  const { path, label } = from as Record<string, unknown>;
  // Only a path inside the app. State is ours, but it comes back from history,
  // and a back link is not the place to follow anything else.
  if (typeof path !== "string" || !path.startsWith("/projects/")) return null;
  if (typeof label !== "string" || !label.trim()) return null;
  return { path, label };
}

/** The file's own screen, carrying where the person came from. */
export function FileLink({
  projectId,
  id,
  from,
  className,
  children,
}: {
  projectId: string;
  id: string;
  from?: CameFrom;
  className?: string;
  children: ReactNode;
}) {
  return (
    <Link
      to={`/projects/${projectId}/brain/files/${id}`}
      state={from ? { from } : undefined}
      className={
        className ?? "font-semibold text-ink underline-offset-2 hover:text-brand hover:underline"
      }
    >
      {children}
    </Link>
  );
}
